/**
 * Compare-depth presets and engine-aware property allow-lists.
 *
 * Depth is a single user-facing dial that combines `detailed` (column-only vs
 * full per-table fetch) and `ignoredProperties` (which volatile props to suppress
 * from the diff). Anything that doesn't match a preset shows up as 'custom'.
 */

import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import type { SchemaCompareOptions } from '../../core/types/workspaceTypes';
import type { TableDetailClass } from '../types/schemaCompare';
import {
  TABLE_DETAIL_CLASS_LABELS,
  TABLE_DETAIL_CLASS_ORDER,
} from '../types/schemaCompare';

export type CompareDepth = 'quick' | 'standard' | 'deep' | 'custom';

/**
 * U6 — the per-table property classes Quick depth (`detailed: false`) can leave
 * unfetched, in the one place every surface that names them reads from.
 *
 * Verified against the fetch code, not restated from copy:
 * `electron/metadataService.ts`'s light-mode early return hands back
 * `constraints: []` / `indexes: []` and populates neither `options` nor
 * `partition`, and `electron/schema-compare/schemaFetcher.ts` gates its
 * trigger fetch on the same `detailed` flag. Column enrichments still run at
 * every depth, which is why columns (and their computed expressions,
 * identity, collation and comments) are NOT on this list.
 *
 * §2.3 — that verification had a hole, and the hole is why this is now the
 * PRE-RUN, general-case list rather than the truth about any given run. It
 * described `metadataService` while `schemaFetcher.ts` forks to
 * `getSqliteTableMetadata` three lines above the call it describes, and that
 * function takes no `detailed` parameter at all: on SQLite, constraints,
 * indexes and table options are read at every depth and only triggers are
 * skipped. Anything reporting on a run that HAPPENED must render
 * `SchemaCompareWarning`'s `classes` — the list the fetch itself recorded —
 * and not this constant.
 *
 * This is a different kind of skip from `ignoredProperties`, and the two must
 * not be merged: an ignored property was fetched and deliberately left out of
 * the diff, and the user can un-tick it in Advanced → "Properties to compare".
 * Nothing in this list was ever read, no checkbox can bring it back, and the
 * only remedy is re-running at Standard or Deep. That difference is why the
 * W5.3 "Skipping: …" summary — which renders from `ignoredProperties` and is
 * empty at Quick — cannot carry this disclosure, and why it travels as a
 * `table-detail-not-compared` warning instead.
 */
export const QUICK_DEPTH_SKIPPED_CLASSES: readonly TableDetailClass[] = TABLE_DETAIL_CLASS_ORDER;

/**
 * `"constraints, indexes, triggers, table options, and partition definitions"`
 * — Oxford comma, matching the wording the public docs page
 * (`website/src/docs/schema-compare.njk`) and the setup dialog's depth card
 * already use, so the three read as one sentence rather than three
 * paraphrases.
 *
 * Takes the classes to name, defaulting to the general-case list above for the
 * PRE-RUN surfaces (the depth card, which describes what Quick will do before
 * an engine is even known). A surface describing a run that already happened
 * passes that run's own `classes`.
 *
 * A single class renders bare ("triggers"), two render "a and b" — an Oxford
 * comma needs three items, and "triggers, and" would read as a typo.
 */
export const quickDepthSkippedList = (
  classes: readonly TableDetailClass[] = QUICK_DEPTH_SKIPPED_CLASSES,
): string => {
  // `?? []` rather than trusting the parameter type: this renders a DISCLOSURE,
  // and a disclosure that throws is strictly worse than one that says less. An
  // unlabelled class is dropped for the same reason.
  const labels = (classes ?? []).map((c) => TABLE_DETAIL_CLASS_LABELS[c]).filter(Boolean);
  if (labels.length === 0) return '';
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
};

/**
 * The Overview's per-category footnote and empty-state phrase. A noun phrase,
 * shaped to sit next to its siblings ("triggers could not be listed", "some
 * table fields could not be read") in both the category header and the
 * `… — so they were never compared.` empty-state sentence.
 *
 * Says what the COMPARISON did, never what the database has: "were skipped",
 * not "there are none".
 *
 * §2.3 — a function of the run's own classes, not a constant. Every class label
 * is plural ("triggers", "table options"), so "were" agrees in the one-class
 * case too — which is now reachable, on SQLite.
 */
export const quickDepthSkippedPhrase = (classes: readonly TableDetailClass[]): string => {
  const list = quickDepthSkippedList(classes);
  return list ? `${list} were skipped at Quick depth` : '';
};

/**
 * Property keys the schema fetcher can emit per engine. Anything not in the
 * engine's list is irrelevant to that engine and shouldn't appear in the UI.
 *
 * This list is what drives the Advanced → "Properties to compare" checklist, so
 * a property an engine's comparers CAN emit but this list omits is a difference
 * Jam reports at the user and the user cannot switch off — at any depth.
 *
 * TWO comparers are fed the same `ignoredProperties` array
 * (`schemaComparer.ts:455-458` → `compareTableOptions`, `schemaComparer.ts:567`
 * → `detectColumnChanges`), so an engine's entry has to cover BOTH:
 *   - its table-option fields (`optionComparer.ts`'s `TABLE_OPTION_FIELDS`), and
 *   - the column enrichments its fetch actually populates
 *     (`metadataService.ts`'s `applyColumnEnrichments`, `sqliteMetadataService.ts`,
 *     `mockSchemaFetcher.ts`).
 *
 * W5.2 fix round 3 added pgsql `fillFactor` after checking the table-option
 * comparer only, and recorded "pgsql was the sole exception". **That claim was
 * false** — the column comparer was never audited. Round 4 closed five more
 * gaps of the identical class (pgsql/mysql/sqlite/mock `computedExpression`,
 * oracle + mock `identitySeed`/`identityIncrement`) and removed one dead
 * checkbox (mysql `compression`, which no MySQL comparer can emit).
 * `tableComparers/allowlistInvariants.test.ts` now derives BOTH directions of
 * that invariant from BOTH comparers' own field lists, for every engine.
 */
export const ENGINE_PROPERTY_ALLOWLIST: Record<DatabaseEngineId, string[]> = {
  mssql: [
    'fillFactor',
    'identitySeed',
    'identityIncrement',
    'dataCompression',
    'durability',
    'memoryOptimized',
    'computedExpression',
    'collation',
    'comment',
  ],
  pgsql: [
    'tablespace',
    'fillFactor',
    'inheritsFrom',
    'unlogged',
    'computedExpression',
    'collation',
    'comment',
  ],
  mysql: [
    'storageEngine',
    'rowFormat',
    'defaultCharset',
    'defaultCollation',
    'autoIncrement',
    'computedExpression',
    'collation',
    'comment',
  ],
  oracle: [
    'pctFree',
    'pctUsed',
    'iniTrans',
    'logging',
    'organization',
    'compression',
    'tablespace',
    'computedExpression',
    'identitySeed',
    'identityIncrement',
    'collation',
    'comment',
  ],
  sqlite: ['withoutRowid', 'strict', 'computedExpression', 'collation', 'comment'],
  kusto: [],
  azurestorage: [],
  // W5.2 fix round 5 (D2) — the mock engine can emit EVERY property, so its
  // list is the whole checklist. Round 4 listed only the five column
  // enrichments, reasoning that mock has no table-options comparer. It does, by
  // proxy: `compareTableOptions` dispatches on the OPTION OBJECT's `engine`
  // discriminant, not the connection's, and `MockTableDef.engineOptions`
  // (`electron/mock-engine/types.ts:104`) is a real `EngineSpecificTableOptions`
  // that `mockSchemaFetcher.ts:103` passes straight through — an in-repo e2e
  // fixture already sets `{ engine: 'mssql', dataCompression: 'PAGE',
  // fillFactor: 80 }` on a mock table
  // (`schemaCompareDiffDetailsDialog.spec.ts:168`). So a mock connection could
  // emit `dataCompression`/`fillFactor`/… with no checkbox to switch them off.
  // Not user-reachable (`mock` is excluded from `UserFacingEngineId`), but it
  // made the guard report "no comparer" for the one engine e2e drives.
  mock: [
    // table options — the union of every engine's, since any shape is reachable
    'dataCompression', 'fillFactor', 'memoryOptimized', 'durability',
    'storageEngine', 'rowFormat', 'defaultCharset', 'defaultCollation', 'autoIncrement',
    'tablespace', 'inheritsFrom', 'unlogged',
    'pctFree', 'pctUsed', 'iniTrans', 'compression', 'logging', 'organization',
    'withoutRowid', 'strict',
    // column enrichments the mock fetcher plumbs (`mockSchemaFetcher.ts:84-88`)
    'computedExpression', 'identitySeed', 'identityIncrement', 'collation',
    'comment',
  ],
};

/**
 * Per-engine "Standard" depth defaults — volatile/storage props that almost
 * never indicate meaningful schema drift. Subset of the engine allow-list.
 *
 * W5.2 fix round 3 — pgsql gained `fillFactor`, matching how mssql already
 * treats the identical property and how oracle treats its equivalents
 * (`pctFree`/`pctUsed`/`iniTrans`). This is a deliberate BEHAVIOUR CHANGE:
 * PostgreSQL Standard now suppresses a fillfactor-only difference where it
 * previously reported it as Modified. Two escape hatches keep it honest —
 * Deep shows it (and Deep is now a real, enabled choice on PostgreSQL for the
 * first time), and the Advanced "Properties to compare" checklist can
 * un-ignore it per comparison now that the allow-list above lists it.
 *
 * W5.2 fix round 5 — `identitySeed`/`identityIncrement` are NOT here for ANY
 * engine, and this is the correction of a false premise, not a preference.
 * Round 4 added them for oracle on the belief that an identity seed drifts like
 * a runtime counter. It does not: what Jam reads on both engines is the
 * DECLARED seed, fixed at creation.
 *
 *   - Oracle — `src/features/database-objects/services/queries/oracleQueries.ts:314-315`
 *     (`columnEnrichments`) regexes
 *     `ALL_TAB_IDENTITY_COLS.IDENTITY_OPTIONS`, whose `START WITH:` string is
 *     built from `sys.idnseq$.startwith` (the creation-time constant) and never
 *     from `s.highwater`. Measured on Oracle 23.26.1.0.0: after 100 inserts
 *     `USER_SEQUENCES.LAST_NUMBER` went 1 → 221 while `IDENTITY_OPTIONS` still
 *     reported `START WITH: 1`; a table declared `(START WITH 1000 INCREMENT
 *     BY 5)` reported 1000 / 5.
 *   - SQL Server — `src/features/database-objects/services/queries/mssqlQueries.ts`
 *     (`columnEnrichments` — cited by name, not by line: #115 wrapped every
 *     per-table builder and moved every line number in that file)
 *     selects
 *     `sys.identity_columns.seed_value` / `increment_value`, not `last_value`.
 *     Measured on Azure SQL Edge 15.0.2000.1574: after 105 inserts
 *     `seed_value` stayed 1 while `last_value` moved to 105; a column declared
 *     `IDENTITY(1000, 5)` reported 1000 / 5.
 *
 * So pre-ignoring them at the DEFAULT depth turned a routine dev-vs-prod ID
 * range convention (`START WITH 1` vs `START WITH 1000 INCREMENT BY 5`) into a
 * table reported **Identical**, with the identity declaration then omitted from
 * the sync script. A false negative in a schema-compare tool is worse than the
 * false positive it was meant to remove. Both are compared at every depth now;
 * the user can still untick either in Advanced → "Properties to compare".
 *
 * What stays is genuinely volatile — allocation/storage tuning that really does
 * drift under load: `fillFactor` (mssql/pgsql), `dataCompression` (mssql),
 * `autoIncrement` (mysql — `information_schema.TABLES.AUTO_INCREMENT` is the
 * NEXT value and really does move), `pctFree`/`pctUsed`/`iniTrans` (oracle).
 *
 * `computedExpression` is deliberately NOT here for any engine either: a changed
 * computed expression is a real schema difference, not storage tuning. It is
 * in every engine's allow-list above (so the user can switch it off per
 * comparison) but no preset makes that choice for them.
 *
 * SQLite stays empty on purpose: its comparer only checks `withoutRowid` and
 * `strict`, which are structural table declarations, not storage tuning. It is
 * the only engine where Standard and Deep are genuinely the same comparison.
 */
const STANDARD_IGNORED_BY_ENGINE: Record<DatabaseEngineId, string[]> = {
  mssql: ['fillFactor', 'dataCompression'],
  pgsql: ['fillFactor'],
  mysql: ['autoIncrement'],
  oracle: ['pctFree', 'pctUsed', 'iniTrans'],
  sqlite: [],
  kusto: [],
  azurestorage: [],
  mock: [],
};

/**
 * Object types each engine supports. SQLite has no procedures or functions.
 *
 * `sequences` is true for EXACTLY the engines whose schema-fetch provider
 * genuinely enumerates sequences from a live connection — SQL Server
 * (`sys.sequences`), PostgreSQL (`pg_sequences`) and Oracle (`ALL_SEQUENCES`).
 * MySQL and SQLite have no SEQUENCE concept and their providers return `[]`,
 * so offering the checkbox there would promise objects that can never appear.
 * The mock engine's fetcher produces no sequences either, so it is false too:
 * a checkbox whose only possible answer is "none" is a control that lies.
 *
 * Keep this in lockstep with the providers. A `true` here without a real
 * `fetchSequenceList` behind it is the exact defect class this row exists to
 * avoid.
 *
 * `userTypes` (S7) is true for SQL Server only — the one engine whose fetch
 * provider implements `fetchUserTypes` (table types, alias types, CLR types).
 * PostgreSQL domains/enums/composites and Oracle object types are real
 * objects too, but no provider enumerates them for Schema Compare yet, so the
 * checkbox would be the same lie the `sequences` row above avoids.
 */
export const ENGINE_OBJECT_TYPES: Record<
  DatabaseEngineId,
  {
    tables: boolean;
    views: boolean;
    procedures: boolean;
    functions: boolean;
    sequences: boolean;
    userTypes: boolean;
  }
> = {
  mssql: { tables: true, views: true, procedures: true, functions: true, sequences: true, userTypes: true },
  pgsql: { tables: true, views: true, procedures: true, functions: true, sequences: true, userTypes: false },
  mysql: { tables: true, views: true, procedures: true, functions: true, sequences: false, userTypes: false },
  oracle: { tables: true, views: true, procedures: true, functions: true, sequences: true, userTypes: false },
  sqlite: { tables: true, views: true, procedures: false, functions: false, sequences: false, userTypes: false },
  // Schema Compare does not support kusto yet (no slice in the kusto engine plan) —
  // conservative all-false so this map is a safe no-op if ever consulted.
  kusto: { tables: false, views: false, procedures: false, functions: false, sequences: false, userTypes: false },
  // Azure Storage has no schema to compare — conservative all-false, never consulted.
  azurestorage: { tables: false, views: false, procedures: false, functions: false, sequences: false, userTypes: false },
  mock: { tables: true, views: true, procedures: true, functions: true, sequences: false, userTypes: false },
};

/**
 * Apply baseline defaults for boolean knobs that have undefined slots.
 * Never overrides an explicit user choice. Defaults:
 *   - ignoreCase=false — case differences are real diffs unless the user
 *     opts in (safer than guessing per engine).
 *   - ignoreWhitespace=true — formatting drift in view/routine bodies is
 *     noise 95% of the time.
 *   - formatSqlForComparison=true — format both sides before classifying so
 *     two routines that differ only in indentation/case collapse to
 *     identical (Jeremy Lindsay 2026-05-14 feedback).
 */
export function applyEngineDefaults(
  options: SchemaCompareOptions,
): SchemaCompareOptions {
  return {
    ...options,
    ignoreCase: options.ignoreCase ?? false,
    ignoreWhitespace: options.ignoreWhitespace ?? true,
    formatSqlForComparison: options.formatSqlForComparison ?? true,
  };
}

/**
 * Resolve the standard-preset ignored list for an engine. Falls back to
 * a sensible default that matches the legacy DEFAULT_IGNORED_PROPERTIES
 * cross-section when engine is unknown.
 */
export function getStandardIgnoredProperties(
  engine: DatabaseEngineId | undefined,
): string[] {
  if (!engine) return ['fillFactor', 'autoIncrement', 'pctFree', 'iniTrans'];
  return [...STANDARD_IGNORED_BY_ENGINE[engine]];
}

/**
 * Does this engine have volatile storage properties for Standard to pre-ignore
 * — i.e. is Deep a genuinely different comparison from Standard here?
 *
 * W5.2 fix round 1 (F-1): `STANDARD_IGNORED_BY_ENGINE` is EMPTY for pgsql,
 * sqlite, kusto and mock, so `applyDepthPreset('standard', …)` and
 * `applyDepthPreset('deep', …)` produce byte-identical options objects on
 * those engines — same fetch, same diff, nothing pre-ignored either way.
 * Every sentence that asserts a Standard-vs-Deep difference is therefore
 * false for a PostgreSQL or SQLite user, and `inferDepth` below can't tell
 * the two apart. Ask this before describing or offering the distinction.
 *
 * Derived from the engine's own list rather than a hardcoded engine set, so
 * adding a volatile property to an engine automatically restores its Deep
 * preset and no second source of truth can drift out of sync.
 */
export function deepDiffersFromStandard(
  engine: DatabaseEngineId | undefined,
): boolean {
  return getStandardIgnoredProperties(engine).length > 0;
}

/**
 * Apply a depth preset on top of existing options. Only depth-controlled
 * fields are touched: `detailed` and `ignoredProperties`. Object-type
 * filters, ignoreCase, ignoreWhitespace, includeSystemSchemas are preserved.
 *
 * 'custom' is a no-op (used purely as an inferred state in the UI).
 */
export function applyDepthPreset(
  depth: CompareDepth,
  current: SchemaCompareOptions,
  engine: DatabaseEngineId | undefined,
): SchemaCompareOptions {
  if (depth === 'custom') return current;
  if (depth === 'quick') {
    return { ...current, detailed: false, ignoredProperties: [] };
  }
  if (depth === 'deep') {
    return { ...current, detailed: true, ignoredProperties: [] };
  }
  // standard
  return {
    ...current,
    detailed: true,
    ignoredProperties: getStandardIgnoredProperties(engine),
  };
}

/**
 * W5.2 (D3, Soporte round 4) — an options object with NEITHER `detailed` nor
 * `ignoredProperties` set at all reads as the Deep preset via `inferDepth`
 * below (full fetch, nothing pre-ignored) purely by accident of both fields
 * being `undefined`. That accident is what "every fresh compare starts at
 * Deep" meant before this fix, even though the docs always claimed Standard.
 *
 * Callers with no better source for a depth choice — no saved per-engine
 * default (`getSchemaCompareDefaultOptions`), no user interaction yet, no
 * "Recently compared" pick — should present/submit the Standard preset
 * instead. A no-op the moment EITHER field is explicitly present (even an
 * empty array or `false`), so it never overrides a real choice: the user's
 * own toggles, a restored recent, or a saved default all win outright.
 */
export function applyStandardDepthFallback(
  options: SchemaCompareOptions,
  engine: DatabaseEngineId | undefined,
): SchemaCompareOptions {
  if (options.detailed !== undefined || options.ignoredProperties !== undefined) {
    return options;
  }
  return applyDepthPreset('standard', options, engine);
}

/**
 * Reverse-map current options back to a preset (or 'custom' if it doesn't fit).
 * Used to highlight which preset radio reflects the current options.
 */
export function inferDepth(
  options: SchemaCompareOptions,
  engine: DatabaseEngineId | undefined,
): CompareDepth {
  const ignored = (options.ignoredProperties ?? []).slice().sort();

  // Quick: detailed off (regardless of ignored, since it's mostly moot)
  if (options.detailed === false) {
    if (ignored.length === 0) return 'quick';
    return 'custom';
  }

  // Nothing pre-ignored. On an engine that HAS a Standard list, that's Deep.
  //
  // W5.2 fix round 1 (F-1): on an engine whose Standard list is empty (pgsql,
  // sqlite) Standard and Deep are the *same* options object, so this branch
  // used to hand every PostgreSQL/SQLite user a highlighted "Deep" card on a
  // fresh dialog while the docs called Standard the default. Resolve the tie
  // toward Standard — the documented default, and the only one of the two
  // that's reachable there. `deepDiffersFromStandard` gates the Deep card in
  // the UI for the same reason, so no user is left clicking a preset this
  // function can never report back.
  if (ignored.length === 0) {
    return deepDiffersFromStandard(engine) ? 'deep' : 'standard';
  }

  // Standard: detailed on, ignored matches engine standard exactly
  const standard = getStandardIgnoredProperties(engine).slice().sort();
  if (
    ignored.length === standard.length &&
    ignored.every((p, i) => p === standard[i])
  ) {
    return 'standard';
  }

  return 'custom';
}
